<script setup lang="ts">
import {
	DATA_ATTRIBUTE_ANIMATION_STATE,
	DATA_ATTRIBUTE_ANIMATION_STATE_ACTIVE,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import BlockEcommerceProduct from '@zyro-inc/site-modules/components/blocks/ecommerce/BlockEcommerceProduct.vue';
import { useBlockEcommerceProduct } from '@zyro-inc/site-modules/components/blocks/ecommerce/useBlockEcommerceProduct';
import { getIsInIframe } from '@zyro-inc/site-modules/utils/getIsInIframe';
import { useProductReviewsList } from '@zyro-inc/site-modules/use/useProductReviewsList';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import { useEcommerce } from '@zyro-inc/site-modules/components/ecommerce/useEcommerce';
import {
	EcommerceProductType,
	EcommerceProduct,
	EcommerceProductPage,
	EcommerceCart,
	GOOGLE_TAG_MANAGER_EVENT,
} from '@zyro-inc/site-modules/types';
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';

import { useLightbox } from '@zyro-inc/site-modules/components/lightbox/useLightbox';
import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import { useCookieBanner } from '@zyro-inc/site-modules/utils/useCookieBanner';
import { useRelatedProducts } from '@zyro-inc/site-modules/use/useRelatedProducts';
import { useAddProduct } from '@zyro-inc/site-modules/use/useAddProduct';
import { DEFAULT_EMPTY_PRODUCT_VALUE } from '@zyro-inc/site-modules/constants/ecommerce';

import {
	onMounted,
	onBeforeUnmount,
	ref,
	watch,
} from 'vue';
import {
	SiteBlock,
	SiteBlocks,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import StoreApi from '@zyro-inc/site-modules/api/StoreApi';
import { isProductPage } from '@zyro-inc/site-modules/utils/ecommerce/common';
import { updateProductJsonLd } from '@zyro-inc/site-modules/utils/getProductJsonLd';
import { getAddToCartEventPayload } from '@zyro-inc/site-modules/utils/googleTagManager';
import { getProductImage } from '@zyro-inc/site-modules/utils/ecommerce/productUtils';
import { analyticsEvent } from '@zyro-inc/site-modules/utils/analyticsEvent';

type Props = {
	blockId: string;
	data: SiteBlock;
	lcp: {
		type?: string;
		id?: string;
	};
	ecommerceTranslations: Record<string, string>;
	isQuickPreview: boolean;
	legacyProductPages: Array<EcommerceProductPage>;
	isCartVisible: boolean;
	currentPageType?: string;
	isInPreviewMode: boolean;
	isMobileView: boolean;
	quickPreviewProductId?: string;
	currentPreviewProductPageId?: string;
	blocks: SiteBlocks;
	isDynamicProductPageEnabled: boolean;
	hideRelatedProducts: boolean;
};

const props = withDefaults(defineProps<Props>(), {
	blockId: '',
	lcp: () => ({}),
	ecommerceTranslations: () => ({}),
	isQuickPreview: false,
	legacyProductPages: () => [],
	isCartVisible: false,
	currentPageType: 'default',
	isInPreviewMode: false,
	hideRelatedProducts: false,
});

const {
	siteId,
	ecommerceStoreId,
	meta,
	productData: globalProductData,
	hasAnalyticsIntegration,
} = useSiteGlobal();

const { areCookiesAllowed } = useCookieBanner(meta, props.isInPreviewMode);

const {
	quantifiedCartItemsList,
	canAddToCart,
	isShoppingCartOpen,
	shoppingCartItems,
	variantsQuantity,
	isCheckoutLoading,
	setShoppingCartOpen,
	setShoppingCartItems,
	setIsCheckoutLoading,
	setSelectedBookingProduct,
	updateVariantsQuantity,
	updateCartData,
	initiateExpressCart,
	createExpressCart,
	getStoreShippingRegions,
	stripePublicKey,
	regions,
	isExpressCheckoutEnabled,
	isProductReviewEnabled,
	getStoreSettingsData,
} = useEcommerceGlobal();
const {
	openEcommerceModal,
	closeEcommerceModal,
	setCreateProductReviewData,
} = useEcommerceModal();
const { initiateCheckout } = useEcommerce();
const { addImagesToLightbox } = useLightbox();
const {
	productId,
	blockStyle,
	blockButtonText,
	blockButtonStyle,
	blockButtonType,
	blockButtonBorderWidth,
	textColorVars,
	imageBorderRadius,
	navigationArrowsColor,
	navigationThumbnailArrowsColor,
	galleryPlacement,
	imageRatio,
	isQuantityPickerEnabled,
	expressCheckoutText,
} = useBlockEcommerceProduct(props);

const {
	fetchProductReviewsChunk,
	productReviewsList,
	count,
} = useProductReviewsList();

const isLoading = ref(!globalProductData.value);
const animationState = ref<string | null>(null);
const product = ref<EcommerceProduct>(globalProductData.value || DEFAULT_EMPTY_PRODUCT_VALUE);
const relatedProductsTitle = ref('');
const expressCartData = ref({} as EcommerceCart);
const isInitializing = ref(false);

const {
	relatedProductsPage,
	relatedProductsPageCount,
	fetchRelatedProducts,
	isRelatedProductsLoading,
	relatedProducts,
} = useRelatedProducts({
	storeId: ecommerceStoreId.value,
	blockId: props.blockId,
});

const { addProductToBag } = useAddProduct({
	blockId: props.blockId,
});

const setAnimationState = () => {
	setTimeout(() => {
		animationState.value = DATA_ATTRIBUTE_ANIMATION_STATE_ACTIVE;
	}, 100);
};

const manageCartOpenState = () => {
	if (isShoppingCartOpen.value) {
		return;
	}

	setShoppingCartOpen(true);
};

const handleImageClick = (index: number) => {
	if (!product.value) return;

	const productMedia = product.value.images || product.value.media || [];
	const aggregatedImages = productMedia.map((image) => ({
		alt: product.value?.title,
		src: image.url,
	}));

	addImagesToLightbox(aggregatedImages, index);
};

const handleBuyButtonClick = async (value: EcommerceProduct[]) => {
	// Need to use await here so that all previous modals would get closed for others to open up
	await closeEcommerceModal();

	if (product.value?.type.value === EcommerceProductType.BOOKING) {
		setSelectedBookingProduct(value[0]);
		openEcommerceModal('EcommerceBookingEventSelect');

		return;
	}

	if (getIsInIframe() || props.isInPreviewMode) {
		openEcommerceModal('EcommerceMessageButtonDisabled');

		return;
	}

	if (props.isCartVisible) {
		setShoppingCartItems([
			...shoppingCartItems.value,
			...value,
		]);

		if (areCookiesAllowed.value && hasAnalyticsIntegration.value) {
			analyticsEvent(GOOGLE_TAG_MANAGER_EVENT.ADD_TO_CART, getAddToCartEventPayload(value[0]));
		}

		manageCartOpenState();
	} else {
		setIsCheckoutLoading(true);

		await initiateCheckout(value);

		setIsCheckoutLoading(false);
	}
};

const handleRelatedProductPageChange = async (page: number) => {
	setAnimationState();

	await fetchRelatedProducts({
		categoryId: product.value?.related_products?.[0]?.related_id,
		page,
	});
};

const fetchProductData = async () => {
	const payload: {storeId: string; productId?: string, slug?: string} = {
		storeId: ecommerceStoreId.value,
	};

	isLoading.value = true;

	try {
		if (
			isProductPage(props.currentPageType)
			&& !props.isQuickPreview
			&& !props.isInPreviewMode
		) {
			payload.slug = window?.location?.pathname?.split('/')?.[1] || '';
		} else if (
			isProductPage(props.currentPageType)
			&& props.isInPreviewMode
			&& props.currentPreviewProductPageId
		) {
			payload.productId = props.currentPreviewProductPageId;
		} else if (productId.value !== -1) {
			payload.productId = productId.value;
		} else {
			// Do not fetch anything if product id is missing
			return;
		}

		const [productData] = await Promise.all([
			StoreApi.getProductByIdOrSlug(payload),
			getStoreSettingsData(),
		]);

		if (isProductReviewEnabled.value) {
			productReviewsList.value = productData.reviewsList?.reviews || [];
			count.value = productData.reviewsList?.count || 0;

			setCreateProductReviewData({
				storeId: ecommerceStoreId.value,
				productId: String(productData.id),
				productTitle: productData.title,
				productImage: getProductImage({
					product: productData,
				}),
			});
		}

		const relatedProduct = productData.related_products?.[0];

		if (relatedProduct && !props.hideRelatedProducts) {
			setAnimationState();

			await fetchRelatedProducts({
				categoryId: relatedProduct?.related_id,
			});
		}

		relatedProductsTitle.value = relatedProduct?.section_title || '';
		await updateVariantsQuantity([productData]);

		if (productData) {
			product.value = productData;

			EventLogApi.logEvent({
				eventName: 'builder_customers.product_page.viewed',
				eventProperties: {
					product_type: product.value?.type.value,
					product_reviews: isProductReviewEnabled.value ? !!Object.keys(product.value?.reviewsAnalytics || {}).length : 'not_enabled',
					similar_products: !!product.value?.related_products?.length,
					express_checkout: isExpressCheckoutEnabled.value,
				},
			});

			if (props.isInPreviewMode || !areCookiesAllowed.value || !hasAnalyticsIntegration.value) {
				return;
			}

			// Firing view_item event right after product data is fetched
			analyticsEvent(GOOGLE_TAG_MANAGER_EVENT.VIEW_ITEM, {
				currency: product.value?.variants?.[0]?.prices?.[0]?.currency_code,
				items: [
					{
						item_id: product.value.id as string,
						item_name: product.value?.title,
						price: (Number(product.value?.variants?.[0]?.prices?.[0]?.amount) || 0) / 100,
					},
				],
			});
		}
	} catch (error) {
		console.error(error);
	} finally {
		isLoading.value = false;
	}
};

const initiateProduct = async () => {
	setAnimationState();

	await fetchProductData();
};

const handleCreateCart = (productData?: EcommerceProduct, isEmpty?: boolean) => {
	const data = productData || product.value;
	const products = isEmpty || !data ? [] : [data];

	createExpressCart(products);
};

const initCart = async () => {
	isInitializing.value = true;
	expressCartData.value = await initiateExpressCart() as EcommerceCart;
	isInitializing.value = false;
};

const handleLoadMoreReviews = async () => {
	await fetchProductReviewsChunk({
		storeId: ecommerceStoreId.value,
		productId: product.value?.id as string,
	});
};

onMounted(() => {
	initiateProduct();

	if (!expressCartData.value?.id && !isInitializing.value && isExpressCheckoutEnabled.value) {
		initCart();
	}
});

watch(isExpressCheckoutEnabled, (isEnabled) => {
	if (isEnabled && !expressCartData.value?.id && !isInitializing.value) {
		initCart();
	}
});

onBeforeUnmount(() => {
	closeEcommerceModal();

	// Reset create product review data on unmount
	// eslint-disable-next-line unicorn/no-useless-undefined
	setCreateProductReviewData(undefined);
});

if (isProductPage(props.currentPageType)) {
	watch(() => props.currentPreviewProductPageId, (newValue, oldValue) => {
		if (newValue !== oldValue) {
			initiateProduct();
		}
	});
	watch(product, () => {
		if (product.value) {
			updateProductJsonLd(product.value, variantsQuantity.value);
		}
	});
}
</script>

<template>
	<BlockEcommerceProduct
		:block-id="blockId"
		:block-style="blockStyle"
		:block-button-text="blockButtonText"
		:block-button-style="blockButtonStyle"
		:block-button-type="blockButtonType"
		:block-button-border-width="blockButtonBorderWidth"
		:text-color-vars="textColorVars"
		:image-border-radius="imageBorderRadius"
		:navigation-arrows-color="navigationArrowsColor"
		:navigation-thumbnail-arrows-color="navigationThumbnailArrowsColor"
		:gallery-placement="galleryPlacement"
		:image-ratio="imageRatio"
		:is-quantity-picker-enabled="isQuantityPickerEnabled"
		:quantified-cart-items-list="quantifiedCartItemsList"
		:is-checkout-loading="isCheckoutLoading"
		:legacy-product-pages="legacyProductPages"
		:is-quick-preview="isQuickPreview"
		:current-page-type="currentPageType"
		:is-mobile-view="isMobileView"
		:product-data="product"
		:can-add-to-cart="canAddToCart"
		:is-loading="isLoading"
		:translations="ecommerceTranslations"
		:is-cart-visible="isCartVisible"
		:is-eager="lcp.type === 'block-ecommerce-product' && lcp.id === blockId"
		:[DATA_ATTRIBUTE_ANIMATION_STATE]="animationState"
		:site-id="siteId"
		:shopping-cart-items="shoppingCartItems"
		:variants-quantity="variantsQuantity"
		:is-express-checkout-enabled="isExpressCheckoutEnabled"
		:express-cart-data="expressCartData"
		:update-cart-data="updateCartData"
		:store-id="ecommerceStoreId"
		:regions="regions"
		:express-checkout-text="expressCheckoutText"
		:related-products="relatedProducts"
		:related-products-title="relatedProductsTitle"
		:related-products-page-count="relatedProductsPageCount"
		:related-products-current-page="relatedProductsPage"
		:is-related-products-loading="isRelatedProductsLoading"
		:is-product-review-enabled="isProductReviewEnabled"
		:product-reviews-list="productReviewsList"
		:stripe-public-key="stripePublicKey"
		@create-express-cart="handleCreateCart"
		@buy-button-click="handleBuyButtonClick"
		@image-click="handleImageClick"
		@open-cart="manageCartOpenState"
		@get-regions="getStoreShippingRegions"
		@related-product-page-changed="handleRelatedProductPageChange"
		@load-more-reviews="handleLoadMoreReviews"
		@add-related-product-to-bag="(product) => addProductToBag({
			product,
			isInPreviewMode,
			isCartVisible,
		})"
	/>
</template>
